import React, { useState } from 'react';
import { AlertTriangle, ArrowDownRight, ArrowRight, ArrowUpRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { Tooltip } from '@/components/ui/tooltip';
import { TONE_DOT, type OverallTone } from '@/components/dashboard/statusTokens';
import { useT } from '@/contexts/I18nContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { rackCategoryRank } from '@/lib/zoneComponents';
import { cn } from '@/lib/utils';
import type {
  PortfolioReportPhotoDto,
  PortfolioZoneReportDto,
  TrafficStatus,
} from '@/api/portfolio';

// Per-zone DAY-report mini card for the «Все филиалы» report view (voice spec
// 2026-07-19 part 3, owner feedback 2026-07-19): a small glanceable светофор —
// one dot line per rack category (canonical category order), a queue line, the
// longest-dirty-table line, a tasks line, a tiny open→close fill graphic, and
// up to two of the day's headline photos (letterboxed, hover captions). Every
// row and dot answers «why this colour» in a hover tooltip. A healthy branch
// collapses to one quiet green line.

const TRAFFIC_TONE: Record<TrafficStatus, OverallTone> = {
  green: 'ok',
  yellow: 'attention',
  red: 'critical',
};

// Standardised in-image captions by photo kind (owner: no free-form BE
// captions on the thumbnails; the exact wording is fixed).
const PHOTO_KIND_KEY: Record<PortfolioReportPhotoDto['kind'], string> = {
  queue: 'portfolio.day.photoQueue',
  layout_drop: 'portfolio.day.photoLayoutDrop',
  table: 'portfolio.day.photoTable',
};

type LayoutEntry = NonNullable<PortfolioZoneReportDto['layout']>[number];

function StatusDot({ tone }: { tone: OverallTone }) {
  return <span className={cn('inline-block size-2.5 shrink-0 rounded-full', TONE_DOT[tone])} />;
}

function fmtTime(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

// ONE hover mechanism for every line and dot on the card: the shared Tooltip
// (hover + keyboard focus). Rows are focusable so the fact is reachable by
// keyboard; on coarse pointers a tap is swallowed (mirrors UnitDotPopover's
// trigger) so the first tap reveals the explanation instead of drilling into
// the zone — the rest of the card stays the drill-down target.
function HoverLine({
  hover,
  as: Tag = 'li',
  className,
  children,
}: {
  hover: string;
  as?: 'li' | 'div' | 'span';
  className?: string;
  children: React.ReactNode;
}) {
  const coarse = useMediaQuery('(pointer: coarse)');
  return (
    <Tooltip content={hover} side="bottom">
      <Tag
        tabIndex={0}
        className={cn(
          'flex items-center gap-1.5 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className,
        )}
        onClick={coarse ? (e: React.MouseEvent) => e.stopPropagation() : undefined}
      >
        {children}
      </Tag>
    </Tooltip>
  );
}

export function ZoneDayMiniCard({
  zone,
  onOpen,
  reportError = false,
}: {
  zone: PortfolioZoneReportDto;
  onOpen: (zoneId: string) => void;
  reportError?: boolean;
}) {
  const { t, lang } = useT();
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';
  const [lightbox, setLightbox] = useState<number | null>(null);

  const id = String(zone.zone_id);
  const quiet = reportError || zone.closed || !zone.report_exists;
  // TODAY has no report yet — the BE sends a cheap status label instead of a
  // (live-computed) verdict. «сегодня ещё идёт» while the day is still logging,
  // «отчёт формируется» once it has closed.
  const todayLabel =
    !zone.closed && !zone.report_exists && zone.today_status
      ? t(zone.today_status === 'ongoing' ? 'portfolio.day.todayOngoing' : 'portfolio.day.todayForming')
      : null;

  // Canonical category order (Alexey, 2026-07-19): Готовая еда → Выпечка →
  // Десерты → Сопутствующая продукция → everything else; alphabetical within
  // a rank. Same order as the dashboard dot rows.
  const layout = [...(zone.layout ?? [])].sort(
    (a, b) =>
      rackCategoryRank(a.rack_type) - rackCategoryRank(b.rack_type) ||
      a.rack_type.localeCompare(b.rack_type, locale),
  );
  const queue = zone.queue;
  const tables = zone.tables;
  const tasks = zone.tasks;
  const photos = zone.photos.slice(0, 2);
  const oc = zone.layout_open_close;
  const hasOpenClose = !quiet && oc != null && oc.open_pct != null && oc.close_pct != null;

  const overdue = tasks?.overdue ?? 0;
  // Tables: the episode count is deliberately NOT a signal (model precision) —
  // the longest-dirty-table fact is the only thing shown or counted.
  const tablesUnavailable = !!tables && (!!tables.error || !!tables.no_data || tables.episodes === null);
  const tablesUnavailableText = `${t('dashboard.cards.tables')} · ${t(tables?.error ? 'dashboard.apiMissing' : 'dashboard.status.noData')}`;
  const longestMin = tablesUnavailable ? null : tables?.longest_min ?? null;
  const hasIssues =
    layout.some((l) => l.status !== 'green') ||
    (queue != null && queue.status !== 'green') ||
    longestMin != null ||
    overdue > 0;
  // A healthy branch must read as ONE quiet green line, not an empty skeleton.
  const compact = !quiet && !hasIssues && !tablesUnavailable && photos.length === 0;

  const headerTone: OverallTone = quiet
    ? 'none'
    : layout.some((l) => l.status === 'red') || queue?.status === 'red' || overdue > 0
      ? 'critical'
      : hasIssues
        ? 'attention'
        : tablesUnavailable ? 'none' : 'ok';

  const headerHover = reportError
    ? t('dashboard.apiMissing')
    : quiet
    ? zone.closed
      ? t('portfolio.day.hoverClosed')
      : todayLabel ?? t('portfolio.day.hoverNoReport')
    : headerTone === 'critical'
      ? t('portfolio.day.hoverDayCritical')
      : headerTone === 'attention'
        ? t('portfolio.day.hoverDayAttention')
        : tablesUnavailable ? tablesUnavailableText : t('portfolio.day.hoverAllDayOk');

  const peakTime = queue ? fmtTime(queue.peak_at, locale) : null;

  // «Why this colour» for a layout category row, built from the day-findings
  // facts the BE attaches to non-green categories.
  const layoutHover = (l: LayoutEntry): string => {
    if (l.status === 'green') return t('portfolio.day.hoverAllDayOk');
    const pct = l.shortage_pct != null && l.shortage_pct > 0 ? Math.round(l.shortage_pct) : null;
    const rawH = l.problem_hours ?? null;
    const h =
      rawH != null && rawH >= 0.05
        ? (rawH < 1 ? Math.round(rawH * 10) / 10 : Math.round(rawH)).toLocaleString(locale)
        : null;
    if (pct != null && h != null) return t('portfolio.day.hoverLayoutBad', { pct, h });
    if (pct != null) return t('portfolio.day.hoverLayoutShortage', { pct });
    if (h != null) return t('portfolio.day.hoverLayoutHours', { h });
    return t('portfolio.day.hoverLayoutIssue');
  };

  const queueHover = queue
    ? queue.peak_people != null
      ? peakTime
        ? t('portfolio.day.hoverQueuePeak', { n: queue.peak_people, time: peakTime })
        : t('portfolio.day.hoverQueuePeakNoTime', { n: queue.peak_people })
      : queue.status === 'green'
        ? t('portfolio.day.hoverAllDayOk')
        : t('portfolio.day.hoverQueueIssue')
    : '';

  // Same framing as the daily report's «Самый долгий неубранный стол».
  const tablesText =
    tablesUnavailable
      ? tablesUnavailableText
      : longestMin != null
      ? tables?.longest_table
        ? t('portfolio.day.longestTable', { name: tables.longest_table, m: longestMin })
        : t('portfolio.day.longestTableNoName', { m: longestMin })
      : null;

  const tasksHover = tasks
    ? t('portfolio.day.hoverTasks', { done: tasks.done, total: tasks.total }) +
      (overdue > 0 ? ` · ${t('portfolio.overdue', { n: overdue })}` : '')
    : '';

  const openCloseRow = hasOpenClose && oc && (() => {
    const openPct = Math.round(oc.open_pct!);
    const closePct = Math.round(oc.close_pct!);
    const delta = closePct - openPct;
    const Arrow = delta < -2 ? ArrowDownRight : delta > 2 ? ArrowUpRight : ArrowRight;
    return (
      <HoverLine
        as="div"
        hover={t('portfolio.day.hoverOpenClose')}
        className="gap-1 text-xs text-muted-foreground"
      >
        <span>
          {t('portfolio.day.atOpen')} {openPct}%
        </span>
        <Arrow
          className={cn(
            'size-3.5 shrink-0',
            delta < -2 ? 'text-amber-500' : delta > 2 ? 'text-success' : 'text-muted-foreground',
          )}
        />
        <span>
          {t('portfolio.day.atClose')} {closePct}%
        </span>
      </HoverLine>
    );
  })();

  const photoLabel = (p: PortfolioReportPhotoDto) => t(PHOTO_KIND_KEY[p.kind]);

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => { if (!document.getSelection()?.toString()) onOpen(id); }}
      onKeyDown={(e) => {
        // Only the card itself — Enter on an inner photo button or focused row
        // must not also drill into the zone.
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') onOpen(id);
      }}
      className="cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <CardContent className="space-y-2.5 p-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-2">
          <h3 className="truncate font-medium">{zone.zone_name}</h3>
          <HoverLine as="span" hover={headerHover} className="shrink-0 rounded-full">
            <StatusDot tone={headerTone} />
          </HoverLine>
        </div>

        {quiet ? (
          <HoverLine as="div" hover={headerHover} className="text-sm text-muted-foreground">
            {reportError ? t('dashboard.apiMissing') : zone.closed ? t('portfolio.zoneClosed') : todayLabel ?? t('portfolio.noReport')}
          </HoverLine>
        ) : compact ? (
          <HoverLine
            as="div"
            hover={t('portfolio.day.hoverAllDayOk')}
            className="text-sm text-success"
          >
            <StatusDot tone="ok" />
            {t('portfolio.allGreen')}
          </HoverLine>
        ) : (
          <>
            {!hasIssues && !tablesUnavailable ? (
              <HoverLine
                as="div"
                hover={t('portfolio.day.hoverAllDayOk')}
                className="text-sm text-success"
              >
                <StatusDot tone="ok" />
                {t('portfolio.allGreen')}
              </HoverLine>
            ) : (
              <ul className="space-y-1 text-sm">
                {layout.map((l, i) => (
                  <HoverLine key={`${l.rack_type}-${i}`} hover={layoutHover(l)}>
                    <StatusDot tone={TRAFFIC_TONE[l.status]} />
                    <span className="truncate">{l.rack_type || t('dashboard.cards.layout')}</span>
                  </HoverLine>
                ))}
                {queue && (
                  <HoverLine hover={queueHover}>
                    <StatusDot tone={TRAFFIC_TONE[queue.status]} />
                    <span className="truncate">
                      {t('dashboard.cards.queue')}
                      {queue.status !== 'green' && queue.peak_people != null && (
                        <span className="text-muted-foreground">
                          {' · '}
                          {peakTime
                            ? t('portfolio.day.queuePeak', { n: queue.peak_people, time: peakTime })
                            : t('portfolio.day.queuePeakNoTime', { n: queue.peak_people })}
                        </span>
                      )}
                    </span>
                  </HoverLine>
                )}
                {tablesText != null && (
                  <HoverLine hover={tablesText}>
                    <StatusDot tone={tablesUnavailable ? 'none' : 'attention'} />
                    <span className="truncate">{tablesText}</span>
                  </HoverLine>
                )}
                {tasks && (tasks.total > 0 || overdue > 0) && (
                  <HoverLine hover={tasksHover}>
                    {overdue > 0 ? (
                      <AlertTriangle className="size-3 shrink-0 text-destructive" />
                    ) : (
                      <StatusDot tone={tasks.done < tasks.total ? 'attention' : 'ok'} />
                    )}
                    <span className="truncate">
                      {t('portfolio.day.tasksLine', { done: tasks.done, total: tasks.total })}
                      {overdue > 0 && (
                        <span className="font-medium text-destructive">
                          {', '}
                          {t('portfolio.overdue', { n: overdue })}
                        </span>
                      )}
                    </span>
                  </HoverLine>
                )}
              </ul>
            )}

            {openCloseRow}

            {photos.length > 0 && (
              <div className={cn('grid gap-2', photos.length > 1 && 'grid-cols-2')}>
                {photos.map((p, i) => (
                  // Letterbox, never crop (mirrors the dashboard carousel):
                  // object-contain on a quiet padded background, standardised
                  // caption INSIDE the image, visible only on hover/focus.
                  <Tooltip key={`${p.kind}-${i}`} content={photoLabel(p)}><button
                    type="button"
                    aria-label={photoLabel(p)}
                    className="group relative block h-24 min-w-0 overflow-hidden rounded-md border border-border bg-muted/40 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightbox(i);
                    }}
                  >
                    <img
                      src={p.image_url}
                      alt={photoLabel(p)}
                      loading="lazy"
                      draggable={false}
                      className="absolute inset-0 size-full object-contain p-1"
                    />
                    <span className="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/60 to-transparent px-2 pb-1 pt-4 text-[11px] font-medium leading-tight text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                      {photoLabel(p)}
                    </span>
                  </button></Tooltip>
                ))}
              </div>
            )}
          </>
        )}

        {lightbox != null && (
          // The lightbox mounts inside the clickable card — fence its clicks
          // and key presses off the card's own onOpen handlers.
          <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <ImageLightbox
              srcs={photos.map((p) => p.image_url)}
              captions={photos.map((p) => {
                const at = fmtTime(p.at, locale);
                return at ? `${photoLabel(p)} · ${at}` : photoLabel(p);
              })}
              index={lightbox}
              onClose={() => setLightbox(null)}
              onNavigate={setLightbox}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
